/************************************************************************************

* [SECTION]: COLLECTION FILTER

*************************************************************************************/
if (!customElements.get('l-collection-filters')) {
	class LTACollectionFilters extends HTMLElement {
		constructor() {
			super()
		}

		connectedCallback() {
			// Elements
			this.facetFormEl = this.querySelector('form')
			this.footerControlsEl = document.getElementById(
				`CollectionFiltersControls-${this.dataset.type}-${this.dataset.deviceVersion}`
			)

			// Data
			this.sectionId = this.dataset.sectionId
			this.hasSortBy = this.facetFormEl.querySelector('select[name="sort_by"]') ? true : false
			this.data = {
				searchParamsInitial: window.location.search.slice(1),
				searchParamsPrev: window.location.search.slice(1),
				filterData: [],
			}
			this.abortController = new AbortController()

			// Events
			this.setupTriggerFilterEvents()
			this.updateFilterCount()
			this.setupPopStateListener()
		}

		setupTriggerFilterEvents() {
			// Submit / input event based on the presence of the footer controls
			if (this.footerControlsEl) {
				// Setup submit event
				this.facetFormEl.addEventListener('submit', (event) => {
					event.preventDefault()

					const newSearchParams = this.createSearchParams(this.facetFormEl)
					this.handleFiltersChange(newSearchParams)
				})
			}

			// Setup input event
			this.facetFormEl.addEventListener(
				'input',
				l_debounce((event) => {
					event.preventDefault()

					const newSearchParams = this.createSearchParams(this.facetFormEl)

					// Compare the search params to avoid unnecessary fetch
					this.dispatchEvent(
						new CustomEvent('l:filters:input', {
							bubbles: true,
							detail: { searchParamsPrev: this.data.searchParamsPrev, newSearchParams },
						})
					)

					// Trigger the filter change
					if (!this.footerControlsEl) this.handleFiltersChange(newSearchParams)
				}, 800)
			)

			// Catch trigger filter
			document.addEventListener('l:filters:triggeredFilter', (event) => {
				const newSearchParams = event.detail.newSearchParams

				this.handleFiltersChange(newSearchParams)
			})
		}

		setupPopStateListener() {
			const onHistoryChange = (event) => {
				const searchParams = event.state ? event.state.searchParams : this.data.searchParamsInitial
				if (searchParams === this.data.searchParamsPrev) return

				this.handleFiltersChange(searchParams, false)
			}

			window.addEventListener('popstate', onHistoryChange)
		}

		handleFiltersChange(newSearchParams, updateURLHash = true) {
			if (this.data.searchParamsPrev === newSearchParams) return

			// Save the previous search params
			this.data.searchParamsPrev = newSearchParams

			// Setup loading state for elements
			this.dispatchEvent(new CustomEvent('l:filters:loading', { bubbles: true }))

			// Get new search URL and handle rendering based on cache or fetch
			const newSearchUrl = `${window.location.pathname}?section_id=${this.sectionId}&${newSearchParams}`
			const filterDataUrl = (element) => element.url === newSearchUrl

			this.data.filterData.some(filterDataUrl)
				? this.renderSectionFromCache(filterDataUrl)
				: this.renderSectionFromFetch(newSearchUrl)

			// Update URL hash
			if (updateURLHash) this.updateURLHash(newSearchParams)

			// Close drawer
			this.filterDrawer = this.filterDrawer || this.closest('l-drawer')
			if (this.filterDrawer) this.filterDrawer.drawer_close()
		}

		renderSectionFromCache(filterDataUrl) {
			const html = this.data.filterData.find(filterDataUrl).html

			this.renderContent(html)
		}

		renderSectionFromFetch(url) {
			this.abortController?.abort()
			this.abortController = new AbortController()

			fetch(url, { signal: this.abortController.signal })
				.then((response) => response.text())
				.then((html) => {
					// Save the new filter data
					this.data.filterData = [...this.data.filterData, { html, url }]

					// Render the new HTML
					this.renderContent(html)
				})
		}

		renderContent(html) {
			const newHTML = l_getSectionDOM(html)

			if (!html) return

			// Replace the current filters with the new filters
			// Replace trigger / content to avoid miss the open state
			this.querySelectorAll('l-collapsible-tab').forEach((element) => {
				const id = element.id
				const newElement = newHTML.querySelector(`#${id}`)

				if (newElement) {
					const triggerEl = newElement.querySelector('[data-trigger]')
					const contentEl = newElement.querySelector('[data-content]')

					element.querySelector('[data-content]').innerHTML = contentEl.innerHTML
					element.querySelector('[data-trigger]').innerHTML = triggerEl.innerHTML
				}
			})

			// Update footer controls
			if (this.footerControlsEl) {
				const newFooterControlsEl = newHTML.querySelector(`#${this.footerControlsEl.id}`)
				this.footerControlsEl.innerHTML = newFooterControlsEl.innerHTML
			}

			// Re-initiate the trigger events
			this.setupTriggerFilterEvents()

			// Dispatch event to allow other components render the the new HTML
			this.dispatchEvent(
				new CustomEvent('l:filters:rendered', {
					bubbles: true,
					detail: {
						html: newHTML,
					},
				})
			)
		}

		updateURLHash(newSearchParams) {
			history.pushState(
				{ searchParams: newSearchParams },
				'',
				`${window.location.pathname}${newSearchParams && '?'.concat(newSearchParams)}`
			)
		}

		createSearchParams(form) {
			const formData = new FormData(form)

			// Remove empty fields
			const removeEmptyFields = ['filter.v.price.lte', 'filter.v.price.gte']
			removeEmptyFields.forEach((key) => {
				if (formData.get(key) === '') formData.delete(key)
			})

			return new URLSearchParams(formData).toString()
		}

		updateFilterCount() {
			this.querySelectorAll('l-collapsible-tab').forEach((tabEl) => {
				tabEl.addEventListener('input', (event) => {
					// Update the sortby element
					const selectedSortbyEl = tabEl.querySelector('input:checked')
					const sortbyEl = tabEl.querySelector('[data-selected-sort-by]')
					if (sortbyEl && selectedSortbyEl) {
						sortbyEl.textContent = selectedSortbyEl.dataset.optionName
					}

					// Update the count element
					const count = tabEl.querySelectorAll('input:checked').length
					const countEl = tabEl.querySelector('[data-filter-count]')

					if (countEl) {
						countEl.textContent = `+${count}`
						countEl.classList.toggle('l-hidden', count === 0)
					}
				})
			})
		}
	}

	customElements.define('l-collection-filters', LTACollectionFilters)
}

/************************************************************************************

* [COMPONENTS]: FILTER COMPONENTS

*************************************************************************************/

if (!customElements.get('l-price-range')) {
	class LTAPriceRange extends HTMLElement {
		constructor() {
			super()

			// Elements
			this.rangeMin = this.querySelector('input[type="range"]:first-child')
			this.rangeMax = this.querySelector('input[type="range"]:last-child')
			this.inputMin = this.querySelector('input[name="filter.v.price.gte"]')
			this.inputMax = this.querySelector('input[name="filter.v.price.lte"]')

			// Events
			this.inputMin.addEventListener('focus', this.inputMin.select)
			this.inputMax.addEventListener('focus', this.inputMax.select)
			this.inputMin.addEventListener('change', this.onInputMinChange.bind(this))
			this.inputMax.addEventListener('change', this.onInputMaxChange.bind(this))

			this.rangeMin.addEventListener('change', this.onRangeMinChange.bind(this))
			this.rangeMax.addEventListener('change', this.onRangeMaxChange.bind(this))
			this.rangeMin.addEventListener('input', this.onRangeMinInput.bind(this))
			this.rangeMax.addEventListener('input', this.onRangeMaxInput.bind(this))
		}

		onInputMinChange(event) {
			event.preventDefault()
			event.target.value = Math.max(
				Math.min(parseInt(event.target.value), parseInt(this.inputMax.value || event.target.max) - 1),
				event.target.min
			)
			this.rangeMin.value = event.target.value
			this.rangeMin.parentElement.style.setProperty(
				'--from',
				`${(parseInt(this.rangeMin.value) / parseInt(this.rangeMin.max)) * 100}%`
			)
		}

		onInputMaxChange(event) {
			event.preventDefault()
			event.target.value = Math.min(
				Math.max(parseInt(event.target.value), parseInt(this.inputMin.value || event.target.min) + 1),
				event.target.max
			)
			this.rangeMax.value = event.target.value
			this.rangeMax.parentElement.style.setProperty(
				'--to',
				`${(parseInt(this.rangeMax.value) / parseInt(this.rangeMax.max)) * 100}%`
			)
		}

		onRangeMinChange(event) {
			event.stopPropagation()
			this.inputMin.value = event.target.value
			this.inputMin.dispatchEvent(new Event('change', { bubbles: true }))
		}

		onRangeMaxChange(event) {
			event.stopPropagation()
			this.inputMax.value = event.target.value
			this.inputMax.dispatchEvent(new Event('change', { bubbles: true }))
		}

		onRangeMinInput(event) {
			event.target.value = Math.min(parseInt(event.target.value), parseInt(this.inputMax.value || event.target.max) - 1)
			event.target.parentElement.style.setProperty(
				'--from',
				`${(parseInt(event.target.value) / parseInt(event.target.max)) * 100}%`
			)
			this.inputMin.value = event.target.value
		}

		onRangeMaxInput(event) {
			event.target.value = Math.max(parseInt(event.target.value), parseInt(this.inputMin.value || event.target.min) + 1)
			event.target.parentElement.style.setProperty(
				'--to',
				`${(parseInt(event.target.value) / parseInt(event.target.max)) * 100}%`
			)
			this.inputMax.value = event.target.value
		}
	}

	customElements.define('l-price-range', LTAPriceRange)
}

if (!customElements.get('l-collection-count')) {
	class LTACollectionCount extends HTMLElement {
		constructor() {
			super()
		}

		connectedCallback() {
			// Events
			document.addEventListener('l:filters:loading', () => {
				this.classList.add('is-loading')
			})

			document.addEventListener('l:filters:rendered', (event) => {
				// Setup loading
				this.classList.remove('is-loading')

				// Update product grid HTML
				const newHTML = event.detail.html
				const newCollectionCount = newHTML.querySelector('l-collection-count')

				if (newCollectionCount) {
					this.innerHTML = newCollectionCount.innerHTML
				}
			})
		}
	}

	customElements.define('l-collection-count', LTACollectionCount)
}

if (!customElements.get('l-filter-footer-controls')) {
	class LTAFilterFooterControls extends HTMLElement {
		constructor() {
			super()
		}

		connectedCallback() {
			// Elements
			this.resetBtnEl = this.querySelector('a')
			this.submitBtnEl = this.querySelector('button[type="submit"]')

			// Events
			document.addEventListener('l:filters:input', (event) => {
				const { searchParamsPrev, newSearchParams } = event.detail

				this.submitBtnEl.classList.toggle('is-disabled', searchParamsPrev == newSearchParams)

				this.submitBtnEl.disabled = searchParamsPrev == newSearchParams

				// if (searchParamsPrev == newSearchParams) this.submitBtnEl.disabled = true
				// else this.submitBtnEl.removeAttribute('disabled')
			})

			document.addEventListener('l:filters:loading', () => {
				this.submitBtnEl.classList.add('is-loading')
			})

			document.addEventListener('l:filters:rendered', (event) => {
				this.submitBtnEl.classList.remove('is-loading')

				// Re-query the elements
				this.resetBtnEl = this.querySelector('a')
				this.submitBtnEl = this.querySelector('button[type="submit"]')
			})

			this.addEventListener('click', (event) => {
				if (this.resetBtnEl.contains(event.target)) {
					event.preventDefault()

					// [PAA] Take the reset params from the link's href instead of hardcoding ''
					// so the search page keeps its ?q= term when clearing filters.
					// Collection pages are unchanged: their reset href has no query string → ''.
					const newSearchParams = this.resetBtnEl.getAttribute('href').split('?')[1] || ''

					this.dispatchEvent(
						new CustomEvent('l:filters:triggeredFilter', {
							bubbles: true,
							detail: { newSearchParams },
						})
					)
				}
			})
		}
	}

	customElements.define('l-filter-footer-controls', LTAFilterFooterControls)
}

if (!customElements.get('l-collection-current-filters')) {
	class LTACollectionCurrentFilters extends HTMLElement {
		constructor() {
			super()
		}

		connectedCallback() {
			// Events
			this.addEventListener('click', (event) => {
				event.preventDefault()

				const target = event.target
				const removeLinkEl = target.closest('a')

				if (removeLinkEl) {
					const newSearchParams = removeLinkEl.getAttribute('href').split('?')[1] || ''
					this.dispatchEvent(
						new CustomEvent('l:filters:triggeredFilter', {
							bubbles: true,
							detail: { newSearchParams },
						})
					)
				}
			})

			document.addEventListener('l:filters:loading', () => {
				this.classList.add('is-loading')
			})

			document.addEventListener('l:filters:rendered', (event) => {
				// Setup loading
				this.classList.remove('is-loading')

				// Update product grid HTML
				const newHTML = event.detail.html
				const newCollectionCount = newHTML.querySelector('l-collection-current-filters')

				if (newCollectionCount) {
					this.innerHTML = newCollectionCount.innerHTML
				}
			})
		}
	}

	customElements.define('l-collection-current-filters', LTACollectionCurrentFilters)
}

/************************************************************************************

* [COMPONENT]: QUICK FILTERS

*************************************************************************************/

if (!customElements.get('l-collection-quick-filters')) {
	class LTACollectionQuickFilters extends HTMLElement {
		constructor() {
			super()
		}

		connectedCallback() {
			document.addEventListener('l:filters:loading', () => {
				this.classList.add('is-loading')
			})

			document.addEventListener('l:filters:rendered', (event) => {
				// Setup loading
				this.classList.remove('is-loading')

				// Update product grid HTML
				const newHTML = event.detail.html
				const newQuickFilters = newHTML.querySelector('l-collection-quick-filters')

				if (newQuickFilters) {
					const oldQuickFilters = Array.from(this.querySelectorAll('[data-quick-filter]'))
					newQuickFilters.querySelectorAll('[data-quick-filter]').forEach((quickFilterEl, index0) => {
						if (oldQuickFilters[index0]) {
							oldQuickFilters[index0].innerHTML = quickFilterEl.innerHTML
							oldQuickFilters[index0].classList.toggle('is-active', quickFilterEl.classList.contains('is-active'))
						}
					})
				}
			})
		}
	}

	customElements.define('l-collection-quick-filters', LTACollectionQuickFilters)
}

/************************************************************************************

* [COMPONENT]: PRODUCT GRID

*************************************************************************************/

if (!customElements.get('l-collection-grid')) {
	class LTACollectionGrid extends HTMLElement {
		constructor() {
			super()
		}

		connectedCallback() {
			// Elements
			this.productGridEl = this.querySelector('[data-product-grid]')

			// Data

			// Events
			document.addEventListener('l:filters:loading', this.handleOnFilterLoading.bind(this))
			document.addEventListener('l:filters:rendered', this.handleOnRenderedFilter.bind(this))
		}

		handleOnFilterLoading(event) {
			this.productGridEl.classList.add('is-loading')
		}

		handleOnRenderedFilter(event) {
			// Update product grid HTML
			const newHTML = event.detail.html
			const newProductGridEl = newHTML.querySelector('[data-product-grid]')

			if (newProductGridEl) {
				this.productGridEl.classList.toggle('l-is-empty', newProductGridEl.classList.contains('l-is-empty'))
				this.productGridEl.innerHTML = newProductGridEl.innerHTML
			}

			// Update pagination HTML
			const newPaginationEl = newHTML.querySelector('[data-pagination]')
			const paginationEl = this.querySelector('[data-pagination]')

			if (paginationEl && newPaginationEl) {
				paginationEl.innerHTML = newPaginationEl.innerHTML
			}

			// Setup loading
			this.productGridEl.classList.remove('is-loading')
		}
	}

	customElements.define('l-collection-grid', LTACollectionGrid)
}

/************************************************************************************

* [COMPONENT]: LOADMORE

*************************************************************************************/

if (!customElements.get('l-collection-pagination')) {
	class LTACollectionPagination extends HTMLElement {
		constructor() {
			super()
		}

		connectedCallback() {
			// Events
			this.addEventListener('click', (event) => {
				const loadMoreBtnEl = event.target.closest('[data-load-more-btn]')

				if (!loadMoreBtnEl) return

				event.preventDefault()

				loadMoreBtnEl.classList.add('is-loading')

				const nextUrl = loadMoreBtnEl.dataset.nextUrl
				const fetchUrl = `${nextUrl}&section_id=${this.dataset.sectionId}`

				fetch(fetchUrl)
					.then((response) => response.text())
					.then((html) => {
						const newHTML = l_getSectionDOM(html)

						// Update product grid
						this.currentProductGridEl = this.currentProductGridEl || document.getElementById('main-product-grid')
						const newProductGridEl = newHTML.querySelector('[data-product-grid]')

						if (this.currentProductGridEl && newProductGridEl) {
							this.currentProductGridEl.innerHTML += newProductGridEl.innerHTML
						}

						// Update pagination HTML
						const newPaginationEl = newHTML.querySelector('[data-pagination]')

						if (newPaginationEl) {
							this.innerHTML = newPaginationEl.innerHTML
						}
					})
					.catch((error) => {
						console.error(error)
					})
			})
		}
	}

	customElements.define('l-collection-pagination', LTACollectionPagination)
}

/************************************************************************************

* [COMPONENT]: L-SEARCH

*************************************************************************************/
class LTASearchGroup extends HTMLElement {
	constructor() {
		super()
	}

	connectedCallback() {
		// Elements
		this.searchInputEl = this.querySelector('[data-search-input]')
		this.searchButtonEl = this.querySelector('[data-search-button]')

		// Data
		this.cachedResults = {}

		// Initialize event listeners
		const debouncedOnChange = l_debounce(() => {
			this.handleSearchInput()
		}, 400)

		this.searchInputEl.addEventListener('input', debouncedOnChange)
	}

	handleSearchInput() {
		// [PAA] Keep the raw terms — hyphenating spaces + double-encoding broke multi-word queries
		const searchTerm = this.searchInputEl.value.trim()

		// [PAA] Don't refetch with an empty q (would render "no results for ''" mid-typing)
		if (!searchTerm) return

		// Fetch new search results
		const currentSearchParams = new URLSearchParams(window.location.search)
		currentSearchParams.set('q', searchTerm)
		currentSearchParams.delete('page')

		this.dispatchEvent(
			new CustomEvent('l:filters:triggeredFilter', {
				bubbles: true,
				detail: { newSearchParams: currentSearchParams.toString() },
			})
		)
	}
}

customElements.define('l-search-group', LTASearchGroup)

